import type {
  ChannelChannel,
  ChannelEditOp,
  ChannelEnvelopeReviewOut,
  ChannelFloor,
  ChannelSample,
  ChannelSnapshotV2 as ChannelSnapshot,
  ChannelSnapshotDisposition,
  ChannelTree,
} from '@/api'
import type { ChannelTreeOut } from '@/components/project/review/channelReviewController'
import { projectChannelEdits } from '@/utils/channelTreeProjection'
import { newRequestId } from '@/utils/uuid'

class ChannelSnapshotEditError extends Error {}

export function channelSnapshotCertSlug(snapshot: ChannelSnapshot): string {
  const basename = snapshot.source.pdf_path.split('/').at(-1)
  return snapshot.tree?.cert_slug ?? basename?.replace(/\.pdf$/i, '') ?? snapshot.source.file_id
}

type ChannelEntityKind = 'floor' | 'channel' | 'sample'

export interface NewSnapshotFloor {
  floor_diagram_id?: string
  floor?: ChannelFloor['floor']
  diagram_page?: ChannelFloor['diagram_page']
  diagram_bounding_box?: ChannelFloor['diagram_bounding_box']
}

export type SnapshotFloorEdit = Partial<
  Pick<ChannelFloor, 'floor' | 'diagram_page' | 'diagram_bounding_box'>
>

export interface NewSnapshotChannel {
  channel_id?: string
  from?: ChannelChannel['from']
  to?: ChannelChannel['to']
}

export type SnapshotChannelEdit = Partial<Pick<ChannelChannel, 'chn_id' | 'from' | 'to'>>

export interface NewSnapshotSample extends Partial<ChannelSample> {
  sample_no: string
}

export type SnapshotSampleEdit = Partial<ChannelSample>

export interface ChannelSnapshotReviewProjection {
  snapshot: ChannelSnapshot
  effectiveContentHash: string
  corrected: boolean
  markedDone: boolean
  qaFindings: ChannelEnvelopeReviewOut['qa_findings']
  assayComparison: ChannelEnvelopeReviewOut['assay_comparison']
  authoritativeRevision: number
  floorDiagrams?: ChannelEnvelopeReviewOut['floor_diagrams']
}

export function cloneChannelSnapshot(snapshot: ChannelSnapshot): ChannelSnapshot {
  const next = cloneSnapshotValue(snapshot)
  // Editing uses one identity name; the declared version remains the API boundary.
  for (const floor of next.tree?.floors ?? []) {
    floor.floor_diagram_id ??= floor.floor_id
    delete floor.floor_id
  }
  return next
}

export function channelSnapshotForApi(snapshot: ChannelSnapshot): ChannelSnapshot {
  const next = cloneChannelSnapshot(snapshot)
  if (next.tree?.schema_version && next.tree.schema_version !== '1.3') {
    for (const floor of next.tree.floors ?? []) {
      floor.floor_id = floor.floor_diagram_id
      delete floor.floor_diagram_id
    }
  }
  return next
}

export function setChannelSnapshotDisposition(
  snapshot: ChannelSnapshot,
  disposition: ChannelSnapshotDisposition,
): ChannelSnapshot {
  const next = cloneChannelSnapshot(snapshot)
  next.disposition = cloneSnapshotValue(disposition)
  return next
}

export function insertSnapshotFloor(
  snapshot: ChannelSnapshot,
  index: number,
  input: NewSnapshotFloor,
): ChannelSnapshot {
  rejectNestedChildren(input, 'channels', 'Insert channels separately with insertSnapshotChannel.')
  const next = cloneChannelSnapshot(snapshot)
  const floors = ensureFloors(next)
  const floor: ChannelFloor = {
    floor_diagram_id: stableId('floor', input.floor_diagram_id),
    floor: input.floor,
    channels: [],
  }
  if (hasOwn(input, 'diagram_page')) floor.diagram_page = input.diagram_page
  if (hasOwn(input, 'diagram_bounding_box')) floor.diagram_bounding_box = input.diagram_bounding_box
  assertUniqueId(next, 'floor', floor.floor_diagram_id)
  floors.splice(clampedIndex(index, floors.length), 0, floor)
  return next
}

export function editSnapshotFloor(
  snapshot: ChannelSnapshot,
  floorId: string,
  changes: SnapshotFloorEdit,
): ChannelSnapshot {
  const next = cloneChannelSnapshot(snapshot)
  const floor = requireFloor(next, floorId)
  if (hasOwn(changes, 'floor')) floor.floor = changes.floor
  if (hasOwn(changes, 'diagram_page')) floor.diagram_page = changes.diagram_page
  if (hasOwn(changes, 'diagram_bounding_box'))
    floor.diagram_bounding_box = changes.diagram_bounding_box
  return next
}

export function moveSnapshotFloor(
  snapshot: ChannelSnapshot,
  floorId: string,
  index: number,
): ChannelSnapshot {
  const next = cloneChannelSnapshot(snapshot)
  const floors = ensureFloors(next)
  const currentIndex = floors.findIndex((floor) => floor.floor_diagram_id === floorId)
  if (currentIndex < 0) throw missing('floor')
  const [floor] = floors.splice(currentIndex, 1)
  floors.splice(clampedIndex(index, floors.length), 0, floor)
  return next
}

export function removeSnapshotFloor(snapshot: ChannelSnapshot, floorId: string): ChannelSnapshot {
  const next = cloneChannelSnapshot(snapshot)
  const floors = ensureFloors(next)
  const index = floors.findIndex((floor) => floor.floor_diagram_id === floorId)
  if (index < 0) throw missing('floor')
  floors.splice(index, 1)
  return next
}

export function insertSnapshotChannel(
  snapshot: ChannelSnapshot,
  floorId: string,
  index: number,
  input: NewSnapshotChannel,
): ChannelSnapshot {
  rejectChannelIdEdit(input)
  rejectNestedChildren(input, 'samples', 'Insert samples separately with insertSnapshotSample.')
  const next = cloneChannelSnapshot(snapshot)
  const floor = requireFloor(next, floorId)
  floor.channels ??= []
  const channelId = stableId('channel', input.channel_id)
  const channel: ChannelChannel = {
    channel_id: channelId,
    chn_id: `New channel ${channelId}`,
    from: input.from,
    samples: [],
    to: input.to,
  }
  assertUniqueId(next, 'channel', channel.channel_id)
  floor.channels.splice(clampedIndex(index, floor.channels.length), 0, channel)
  return next
}

export function editSnapshotChannel(
  snapshot: ChannelSnapshot,
  channelId: string,
  changes: SnapshotChannelEdit,
): ChannelSnapshot {
  rejectChannelIdEdit(changes)
  const next = cloneChannelSnapshot(snapshot)
  const channel = requireChannel(next, channelId).channel
  if (hasOwn(changes, 'from')) channel.from = changes.from
  if (hasOwn(changes, 'to')) channel.to = changes.to
  return next
}

export function moveSnapshotChannel(
  snapshot: ChannelSnapshot,
  channelId: string,
  destinationFloorId: string,
  index: number,
): ChannelSnapshot {
  const next = cloneChannelSnapshot(snapshot)
  const source = requireChannel(next, channelId)
  const destination = requireFloor(next, destinationFloorId)
  source.floor.channels ??= []
  destination.channels ??= []
  const sourceIndex = source.floor.channels.indexOf(source.channel)
  source.floor.channels.splice(sourceIndex, 1)
  destination.channels.splice(clampedIndex(index, destination.channels.length), 0, source.channel)
  return next
}

export function removeSnapshotChannel(
  snapshot: ChannelSnapshot,
  channelId: string,
): ChannelSnapshot {
  const next = cloneChannelSnapshot(snapshot)
  const source = requireChannel(next, channelId)
  source.floor.channels!.splice(source.floor.channels!.indexOf(source.channel), 1)
  return next
}

export function insertSnapshotSample(
  snapshot: ChannelSnapshot,
  channelId: string,
  index: number,
  input: NewSnapshotSample,
): ChannelSnapshot {
  const next = cloneChannelSnapshot(snapshot)
  const channel = requireChannel(next, channelId).channel
  channel.samples ??= []
  const sample = cloneSnapshotValue(input)
  sample.sample_id = stableId('sample', sample.sample_id)
  assertUniqueId(next, 'sample', sample.sample_id)
  channel.samples.splice(clampedIndex(index, channel.samples.length), 0, sample)
  return next
}

export function editSnapshotSample(
  snapshot: ChannelSnapshot,
  sampleId: string,
  changes: SnapshotSampleEdit,
): ChannelSnapshot {
  rejectStableIdEdit(changes, 'sample_id', 'sample')
  const next = cloneChannelSnapshot(snapshot)
  const sample = requireSample(next, sampleId).sample
  Object.assign(sample, cloneSnapshotValue(changes))
  return next
}

export function moveSnapshotSample(
  snapshot: ChannelSnapshot,
  sampleId: string,
  destinationChannelId: string,
  index: number,
): ChannelSnapshot {
  const next = cloneChannelSnapshot(snapshot)
  const source = requireSample(next, sampleId)
  const destination = requireChannel(next, destinationChannelId).channel
  source.channel.samples ??= []
  destination.samples ??= []
  source.channel.samples.splice(source.index, 1)
  destination.samples.splice(clampedIndex(index, destination.samples.length), 0, source.sample)
  return next
}

export function removeSnapshotSample(snapshot: ChannelSnapshot, sampleId: string): ChannelSnapshot {
  const next = cloneChannelSnapshot(snapshot)
  const source = requireSample(next, sampleId)
  source.channel.samples!.splice(source.index, 1)
  return next
}

export function channelSnapshotsEqual(
  left: ChannelSnapshot | null,
  right: ChannelSnapshot | null,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right)
}

export function channelSnapshotToTreeOut(
  review: ChannelEnvelopeReviewOut | ChannelSnapshotReviewProjection,
  authoritativeRevision = 0,
): ChannelTreeOut {
  const snapshot = cloneChannelSnapshot(review.snapshot)
  const projected =
    'effective_content_hash' in review
      ? {
          effectiveContentHash: review.effective_content_hash,
          corrected: review.corrected,
          markedDone: review.marked_done,
          qaFindings: review.qa_findings,
          assayComparison: review.assay_comparison,
          authoritativeRevision,
          floorDiagrams: review.floor_diagrams,
        }
      : review
  const treePresent = snapshot.tree !== null
  return {
    cert_slug: channelSnapshotCertSlug(snapshot),
    revision: projected.effectiveContentHash,
    marked_done: projected.markedDone,
    correction_count: projected.corrected ? 1 : 0,
    draft_revision: projected.corrected ? projected.authoritativeRevision : null,
    tree_present: treePresent,
    qa_status: 'available',
    qa_findings: cloneSnapshotValue(projected.qaFindings),
    assay_comparison: cloneSnapshotValue(projected.assayComparison),
    floorDiagrams: projectReviewedFloorDiagrams(snapshot, projected.floorDiagrams),
    channel_disposition: {
      status: snapshot.disposition.status,
      reason: snapshot.disposition.reason ?? null,
      note: snapshot.disposition.note ?? null,
      revision: projected.authoritativeRevision,
    },
    source: {
      status: 'available',
      pdf_path: snapshot.source.pdf_path,
      page_count: snapshot.evidence_pages.length,
      pages: snapshot.evidence_pages.map((page) => ({
        page: page.page,
        ...(evidenceCrop(page.payload) ? { crop: evidenceCrop(page.payload) } : {}),
      })),
    },
    parent: {
      status: 'linked',
      extraction_id: snapshot.assay_parent.file_id,
      ref: null,
    },
    tree: cloneSnapshotValue(
      snapshot.tree ?? { cert_slug: channelSnapshotCertSlug(snapshot), floors: [] },
    ),
  }
}

/**
 * Applies the legacy review command vocabulary to a native snapshot while retaining the native
 * envelope bindings and the system-owned labels of every surviving channel.
 */
const STRUCTURAL_CHANNEL_OPERATIONS: ReadonlySet<ChannelEditOp['op']> = new Set([
  'reassign_sample',
  'add_floor',
  'remove_floor',
  'add_channel',
  'remove_channel',
  'set_channel_floor',
  'set_floor_label',
  'set_floor_location',
  'set_sample_fields',
  'add_sample',
  'remove_sample',
  'clear_floors',
])

export function applyChannelEditOp(
  snapshot: ChannelSnapshot,
  operation: ChannelEditOp,
): ChannelSnapshot {
  if (!snapshot.tree) {
    throw new ChannelSnapshotEditError('Restore the channel diagram before editing its structure.')
  }
  if (operation.op === 'set_channel_label') return applySystemChannelLabel(snapshot, operation)

  const normalized = withStableCreationId(operation)
  if (STRUCTURAL_CHANNEL_OPERATIONS.has(normalized.op))
    return projectSnapshotEdit(snapshot, normalized)
  throw new ChannelSnapshotEditError('Unsupported channel edit operation.')
}

function projectReviewedFloorDiagrams(
  snapshot: ChannelSnapshot,
  sources: ChannelEnvelopeReviewOut['floor_diagrams'],
): ChannelEnvelopeReviewOut['floor_diagrams'] {
  if (sources == null) return null
  return (snapshot.tree?.floors ?? []).flatMap((floor) => {
    const original = sources.find((source) => source.floor_diagram_id === floor.floor_diagram_id)
    if (!hasOwn(floor, 'diagram_page')) return original ? [cloneSnapshotValue(original)] : []
    if (!floor.diagram_page || !floor.floor_diagram_id) return []
    return [
      {
        floor_diagram_id: floor.floor_diagram_id,
        file_id: snapshot.source.file_id,
        content_hash: snapshot.source.content_hash,
        pdf_path: snapshot.source.pdf_path,
        unit_target: original?.unit_target ?? null,
        diagram_page: floor.diagram_page,
        bounding_box: cloneSnapshotValue(floor.diagram_bounding_box ?? null),
        channel_ids: (floor.channels ?? []).flatMap((channel) =>
          channel.channel_id ? [channel.channel_id] : [],
        ),
      },
    ]
  })
}

function evidenceCrop(payload: unknown): Record<string, number> | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null
  const crop = (payload as { crop?: unknown }).crop
  if (!crop || typeof crop !== 'object' || Array.isArray(crop)) return null
  const entries = Object.entries(crop)
  if (!entries.length || entries.some(([, value]) => typeof value !== 'number')) return null
  return Object.fromEntries(entries) as Record<string, number>
}

function withStableCreationId(operation: ChannelEditOp): ChannelEditOp {
  if (operation.save_id || !['add_floor', 'add_channel', 'add_sample'].includes(operation.op)) {
    return operation
  }
  return { ...operation, save_id: newRequestId() }
}

function projectSnapshotEdit(snapshot: ChannelSnapshot, operation: ChannelEditOp): ChannelSnapshot {
  const next = cloneChannelSnapshot(snapshot)
  const labels = new Map(
    (snapshot.tree?.floors ?? []).flatMap((floor) =>
      (floor.channels ?? []).flatMap((channel) =>
        channel.channel_id ? [[channel.channel_id, channel.chn_id] as const] : [],
      ),
    ),
  )
  next.tree = projectChannelEdits(next.tree!, [operation])
  restoreSurvivingChannelLabels(next.tree, labels)
  return next
}

function restoreSurvivingChannelLabels(
  tree: ChannelTree,
  originalLabels: ReadonlyMap<string, string>,
): void {
  const channels = (tree.floors ?? []).flatMap((floor) => floor.channels ?? [])
  const claimed = new Set<string>()
  for (const channel of channels) {
    const originalLabel = channel.channel_id ? originalLabels.get(channel.channel_id) : undefined
    if (originalLabel === undefined) continue
    if (!originalLabel) throw new ChannelSnapshotEditError('CHN_ID is required.')
    if (claimed.has(originalLabel)) {
      throw new ChannelSnapshotEditError(`duplicate CHN_ID '${originalLabel}'`)
    }
    channel.chn_id = originalLabel
    claimed.add(originalLabel)
  }

  let nextLabel = 1
  for (const channel of channels) {
    if (channel.channel_id && originalLabels.has(channel.channel_id)) continue
    while (claimed.has(`Channel ${nextLabel}`)) nextLabel += 1
    channel.chn_id = `Channel ${nextLabel}`
    claimed.add(channel.chn_id)
    nextLabel += 1
  }
}

function applySystemChannelLabel(
  snapshot: ChannelSnapshot,
  operation: ChannelEditOp,
): ChannelSnapshot {
  const channel = (snapshot.tree?.floors ?? [])
    .flatMap((floor) => floor.channels ?? [])
    .find((candidate) => candidate.channel_id === operation.channel_id)
  if (!channel) throw new ChannelSnapshotEditError('channel not found')
  if (!operation.chn_id) {
    throw new ChannelSnapshotEditError('set_channel_label needs a known channel_id and chn_id')
  }
  if (channel.chn_id !== operation.chn_id) {
    throw new ChannelSnapshotEditError('CHN_ID is system-owned and cannot be changed.')
  }
  return cloneChannelSnapshot(snapshot)
}

function cloneSnapshotValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function ensureTree(snapshot: ChannelSnapshot): ChannelTree {
  snapshot.tree ??= {
    cert_slug: channelSnapshotCertSlug(snapshot),
    floors: [],
  }
  return snapshot.tree
}

function ensureFloors(snapshot: ChannelSnapshot): ChannelFloor[] {
  const tree = ensureTree(snapshot)
  tree.floors ??= []
  return tree.floors
}

function stableId(kind: ChannelEntityKind, requested: string | undefined): string {
  return requested ?? `${kind}-${newRequestId()}`
}

function clampedIndex(index: number, length: number): number {
  return Math.max(0, Math.min(index, length))
}

function hasOwn<T extends object, K extends PropertyKey>(
  value: T,
  key: K,
): value is T & Record<K, unknown> {
  return Object.prototype.hasOwnProperty.call(value, key)
}

function rejectStableIdEdit<T extends object>(
  value: T,
  key: keyof T,
  entity: ChannelEntityKind,
): void {
  if (hasOwn(value, key)) {
    throw new ChannelSnapshotEditError(`${entity}_id is a stable identity and cannot be changed.`)
  }
}

function rejectChannelIdEdit(value: object): void {
  if (hasOwn(value, 'chn_id')) {
    throw new ChannelSnapshotEditError('CHN_ID is system-owned and cannot be changed.')
  }
}

function rejectNestedChildren(value: object, key: string, message: string): void {
  if (hasOwn(value, key)) throw new ChannelSnapshotEditError(message)
}

function missing(entity: ChannelEntityKind): ChannelSnapshotEditError {
  return new ChannelSnapshotEditError(`The selected channel ${entity} no longer exists.`)
}

function requireFloor(snapshot: ChannelSnapshot, floorId: string): ChannelFloor {
  const floor = ensureFloors(snapshot).find((candidate) => candidate.floor_diagram_id === floorId)
  if (!floor) throw missing('floor')
  return floor
}

function requireChannel(
  snapshot: ChannelSnapshot,
  channelId: string,
): { floor: ChannelFloor; channel: ChannelChannel } {
  for (const floor of ensureFloors(snapshot)) {
    const channel = floor.channels?.find((candidate) => candidate.channel_id === channelId)
    if (channel) return { floor, channel }
  }
  throw missing('channel')
}

function requireSample(
  snapshot: ChannelSnapshot,
  sampleId: string,
): { channel: ChannelChannel; index: number; sample: ChannelSample } {
  for (const floor of ensureFloors(snapshot)) {
    for (const channel of floor.channels ?? []) {
      const index = channel.samples?.findIndex((sample) => sample.sample_id === sampleId) ?? -1
      if (index >= 0) return { channel, index, sample: channel.samples![index] }
    }
  }
  throw missing('sample')
}

function assertUniqueId(
  snapshot: ChannelSnapshot,
  kind: ChannelEntityKind,
  candidateId: string | undefined,
): void {
  if (!candidateId) throw new ChannelSnapshotEditError(`${kind}_id is required.`)
  if (entityIds(snapshot, kind).has(candidateId)) {
    throw new ChannelSnapshotEditError(`${kind}_id must be unique.`)
  }
}

function entityIds(snapshot: ChannelSnapshot, kind: ChannelEntityKind): Set<string> {
  const ids = new Set<string>()
  for (const floor of snapshot.tree?.floors ?? []) {
    if (kind === 'floor' && floor.floor_diagram_id) ids.add(floor.floor_diagram_id)
    for (const channel of floor.channels ?? []) {
      if (kind === 'channel' && channel.channel_id) ids.add(channel.channel_id)
      for (const sample of channel.samples ?? []) {
        if (kind === 'sample' && sample.sample_id) ids.add(sample.sample_id)
      }
    }
  }
  return ids
}
